import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class CozyBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.tiers = Array.from(this.querySelectorAll('[data-bundle-tier]'));
    this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'));
    this.button = this.querySelector('[data-bundle-submit]');
    this.buttonText = this.querySelector('[data-bundle-button-text]');
    this.error = this.querySelector('[data-bundle-error]');
    this.live = this.querySelector('[data-bundle-live]');

    this.tiers.forEach((tier) => tier.addEventListener('change', () => this.render()));
    this.button?.addEventListener('click', () => this.addBundle());
    this.render();
  }

  get quantity() {
    return Number(this.tiers.find((tier) => tier.checked)?.value || 1);
  }

  render() {
    const quantity = this.quantity;
    this.tiers.forEach((tier) => tier.closest('.cozy-bundle__tier')?.classList.toggle('is-selected', tier.checked));
    this.rows.forEach((row, index) => {
      row.hidden = index >= quantity;
      const select = row.querySelector('[data-bundle-variant]');
      if (select) select.disabled = index >= quantity;
    });

    if (this.buttonText) {
      this.buttonText.textContent = quantity === 1 ? 'Agregar 1 sweater al carrito' : `Agregar pack de ${quantity} al carrito`;
    }
    if (this.error) this.error.hidden = true;
  }

  async addBundle() {
    const activeRows = this.rows.slice(0, this.quantity);
    const selectedIds = activeRows.map((row) => row.querySelector('[data-bundle-variant]')?.value).filter(Boolean);

    if (selectedIds.length !== this.quantity) {
      this.showError('Selecciona un color para cada sweater.');
      return;
    }

    const grouped = new Map();
    selectedIds.forEach((id) => grouped.set(id, (grouped.get(id) || 0) + 1));
    const items = Array.from(grouped, ([id, quantity]) => ({ id: Number(id), quantity }));
    const eventItems = Array.from(grouped, ([id, quantity]) => ({ merchandiseId: id, quantity }));
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : null)
      .filter(Boolean);

    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    if (this.error) this.error.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cozy-bundle',
      lines: eventItems,
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'No pudimos agregar el pack.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: this.quantity,
          productId: this.dataset.productId,
          sections: result.sections,
          source: 'cozy-bundle',
          didError: false,
        },
      });

      if (this.live) this.live.textContent = 'Pack agregado al carrito.';
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'No pudimos agregar el pack.',
        code: 'INVALID',
      }));
      this.showError(error?.message || 'No pudimos agregar el pack. Inténtalo nuevamente.');
    } finally {
      this.button.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }
}

if (!customElements.get('cozy-bundle')) {
  customElements.define('cozy-bundle', CozyBundle);
}